import type { SavingGoal, SavingGoalContribution, GoalSpending } from "../types";
import { roundMoney } from "./money-math";

/** Resolves the list of YYYY-MM calendar months from start date up to asOfMonth. */
export function getGoalMonths(startDate: string, asOfMonth: string): string[] {
  if (!startDate || !asOfMonth) return [];
  const start = startDate.slice(0, 7);
  if (start > asOfMonth) return [];

  const [startYear, startMonth] = start.split("-").map(Number);
  const [endYear, endMonth] = asOfMonth.split("-").map(Number);

  const months: string[] = [];
  let y = startYear;
  let m = startMonth;

  while (y < endYear || (y === endYear && m <= endMonth)) {
    months.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }

  return months;
}

/** Assigned contribution month, falling back to its recorded date. */
export const goalContributionMonth = (contribution: SavingGoalContribution): string =>
  contribution.month || contribution.date.slice(0, 7);

/** Completion releases reserves from its effective month; earlier views retain them. */
export function goalIsCompleted(goal: SavingGoal, month = new Date().toISOString().slice(0, 7)): boolean {
  return !!goal.completedDate && goal.completedDate.slice(0, 7) <= month;
}

/** Sum of net contributions allocated to a goal, including auto-accruals up to asOfMonth. */
export function goalCurrentSaved(goal: SavingGoal, asOfMonth?: string): number {
  if (goal.completedDate) {
    const completedMonth = goal.completedDate.slice(0, 7);
    if (goal.completion && (!asOfMonth || asOfMonth >= completedMonth)) return goal.completion.savedAmount;
    asOfMonth = asOfMonth && asOfMonth < completedMonth ? asOfMonth : completedMonth;
  }
  let total = 0;

  // 1. Explicit ad-hoc contributions and releases
  for (const c of goal.contributions) {
    if (c.deletedAt || (asOfMonth && goalContributionMonth(c) > asOfMonth)) continue;
    if (c.kind === "release") {
      total -= c.amount;
    } else if (c.kind === "ad_hoc") {
      total += c.amount;
    }
  }

  // 2. Scheduled amounts (auto-accrued across elapsed/current months unless skipped)
  if (goal.schedules.length > 0 && goal.startDate) {
    const endMonth = asOfMonth ?? new Date().toISOString().slice(0, 7);
    const months = getGoalMonths(goal.startDate, endMonth);
    const activeSchedules = goal.schedules.filter((s) => s.active);
    const monthlyScheduled = roundMoney(
      activeSchedules.reduce((sum, s) => sum + s.monthlyAmount, 0),
    );

    for (const m of months) {
      const monthContribs = goal.contributions.filter((c) => c.month === m);
      const isSkipped = monthContribs.some((c) => c.kind === "skip" && !c.deletedAt);
      if (isSkipped) continue;

      const explicitScheduled = monthContribs
        .filter((c) => c.kind === "scheduled" && !c.deletedAt)
        .reduce((sum, c) => sum + c.amount, 0);

      if (monthContribs.some((c) => c.kind === "scheduled")) {
        total += explicitScheduled;
      } else if (monthlyScheduled > 0 && (goal.status === "active" || goal.status === "achieved")) {
        total += monthlyScheduled;
      }
    }
  } else {
    for (const c of goal.contributions) {
      if (c.deletedAt || (asOfMonth && goalContributionMonth(c) > asOfMonth)) continue;
      if (c.kind === "scheduled") {
        total += c.amount;
      }
    }
  }

  return Math.max(0, roundMoney(total));
}

/** Total actual spending recorded against a goal from posted expenses. */
export function goalTotalSpent(goal: SavingGoal, transactions: GoalSpending[], asOfMonth?: string): number {
  let spent = 0;
  for (const t of transactions) {
    if (t.goalId === goal.id && (!t.currency || t.currency === goal.currency) && t.type.toLowerCase() === "expense" && (!asOfMonth || t.date.slice(0, 7) <= asOfMonth)) {
      spent += Math.abs(t.amount);
    }
  }
  return roundMoney(spent);
}

export interface GoalMonthStatus {
  month: string;
  scheduledAmount: number;
  contributedAmount: number;
  isSkipped: boolean;
  isComplete: boolean;
  contributions: SavingGoalContribution[];
}

/** Status of a goal for a specific calendar month (e.g. '2026-10'). */
export function goalMonthStatus(goal: SavingGoal, month: string, asOfMonth?: string): GoalMonthStatus {
  if (goal.completedDate && month > goal.completedDate.slice(0, 7)) {
    return { month, scheduledAmount: 0, contributedAmount: 0, isSkipped: false, isComplete: true, contributions: [] };
  }
  const scheduledAmount = roundMoney(
    goal.schedules
      .filter((s) => s.active)
      .reduce((sum, s) => sum + s.monthlyAmount, 0),
  );

  const monthContribs = goal.contributions.filter((c) => c.month === month);
  const isSkipped = monthContribs.some((c) => c.kind === "skip" && !c.deletedAt);

  const explicitScheduled = monthContribs
    .filter((c) => c.kind === "scheduled" && !c.deletedAt)
    .reduce((sum, c) => sum + c.amount, 0);

  const adHocAmount = roundMoney(
    monthContribs
      .filter((c) => c.kind === "ad_hoc" && !c.deletedAt)
      .reduce((sum, c) => sum + c.amount, 0),
  );

  let scheduledContributed = 0;
  if (!isSkipped) {
    if (monthContribs.some((c) => c.kind === "scheduled")) {
      scheduledContributed = explicitScheduled;
    } else if (scheduledAmount > 0 && (goal.status === "active" || goal.status === "achieved")) {
      const currentOrPast = asOfMonth ? month <= asOfMonth : true;
      const startMonth = goal.startDate ? goal.startDate.slice(0, 7) : month;
      if (currentOrPast && month >= startMonth) {
        scheduledContributed = scheduledAmount;
      }
    }
  }

  const contributedAmount = roundMoney(scheduledContributed + adHocAmount);
  const isComplete = !isSkipped && (scheduledAmount === 0 || contributedAmount >= scheduledAmount);

  return {
    month,
    scheduledAmount,
    contributedAmount,
    isSkipped,
    isComplete,
    contributions: monthContribs.filter((c) => !c.deletedAt),
  };
}

export interface GoalPacing {
  currentSaved: number;
  targetAmount: number;
  remainingAmount: number;
  percentComplete: number;
  monthlyCommitment: number;
  projectedCompletionMonth: string | null;
  monthsRemaining: number | null;
  isOnTrack: boolean;
}

/** Pacing, percentage, and completion projections for a goal relative to today. */
export function goalPacing(goal: SavingGoal, today: string): GoalPacing {
  const currentMonth = today.slice(0, 7);
  const currentSaved = goalCurrentSaved(goal, currentMonth);
  const targetAmount = goal.targetAmount;
  const remainingAmount = Math.max(0, roundMoney(targetAmount - currentSaved));
  const percentComplete = targetAmount > 0
    ? Math.min(100, Math.round((currentSaved / targetAmount) * 100))
    : 0;

  const monthlyCommitment = roundMoney(
    goal.schedules
      .filter((s) => s.active && !goalIsCompleted(goal, currentMonth))
      .reduce((sum, s) => sum + s.monthlyAmount, 0),
  );

  let projectedCompletionMonth: string | null = null;
  let monthsRemaining: number | null = null;

  if (remainingAmount <= 0) {
    monthsRemaining = 0;
  } else if (monthlyCommitment > 0) {
    monthsRemaining = Math.ceil(remainingAmount / monthlyCommitment);
    const [yearStr, monthStr] = currentMonth.split("-");
    let y = parseInt(yearStr, 10);
    let m = parseInt(monthStr, 10);
    m += monthsRemaining;
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    projectedCompletionMonth = `${y}-${String(m).padStart(2, "0")}`;
  }

  let isOnTrack = true;
  if (goal.targetDate && remainingAmount > 0) {
    const targetMonth = goal.targetDate.slice(0, 7);
    if (projectedCompletionMonth && projectedCompletionMonth > targetMonth) {
      isOnTrack = false;
    }
  }

  return {
    currentSaved,
    targetAmount,
    remainingAmount,
    percentComplete,
    monthlyCommitment,
    projectedCompletionMonth,
    monthsRemaining,
    isOnTrack,
  };
}

export interface AccountAllocationSummary {
  accountTotal: number;
  goalReserved: number;
  cushionAmount: number;
  safeToSpend: number;
  totalAllocated: number;
  shortfall: number;
  goalPercent: number;
  cushionPercent: number;
  safePercent: number;
  shortfallPercent: number;
}

/**
 * Computes the defrag-style allocation breakdown for an account across earmarked goals,
 * safety cushion floor, and remaining safe-to-spend balance relative to the account total.
 */
export function accountAllocationSummary(
  accountBalance: number,
  goalReserved: number,
  cushionAmount: number,
): AccountAllocationSummary {
  const accountTotal = Math.max(0, roundMoney(accountBalance));
  const reserved = Math.max(0, roundMoney(goalReserved));
  const cushion = Math.max(0, roundMoney(cushionAmount));
  const totalAllocated = roundMoney(reserved + cushion);
  const safeToSpend = Math.max(0, roundMoney(accountTotal - totalAllocated));
  const shortfall = Math.max(0, roundMoney(totalAllocated - accountTotal));

  if (accountTotal <= 0) {
    return {
      accountTotal,
      goalReserved: reserved,
      cushionAmount: cushion,
      safeToSpend: 0,
      totalAllocated,
      shortfall,
      goalPercent: 0,
      cushionPercent: 0,
      safePercent: 0,
      shortfallPercent: 0,
    };
  }

  // The allocation bar strictly portrays the account's actual balance (100% basis)
  const fundedGoal = Math.min(reserved, accountTotal);
  const remainingForCushion = Math.max(0, roundMoney(accountTotal - fundedGoal));
  const fundedCushion = Math.min(cushion, remainingForCushion);

  const goalPercent = Math.round((fundedGoal / accountTotal) * 1000) / 10;
  const cushionPercent = Math.round((fundedCushion / accountTotal) * 1000) / 10;
  const safePercent = Math.max(0, Math.round((100 - goalPercent - cushionPercent) * 10) / 10);

  return {
    accountTotal,
    goalReserved: reserved,
    cushionAmount: cushion,
    safeToSpend,
    totalAllocated,
    shortfall,
    goalPercent,
    cushionPercent,
    safePercent,
    shortfallPercent: 0,
  };
}

export interface FloatingPoolSummary {
  groupName: string;
  totalSaved: number;
  totalTarget: number;
  goals: SavingGoal[];
}

/** Groups floating goals and calculates their combined shared pool balance. */
export function floatingPoolSummaries(goals: SavingGoal[] | undefined): FloatingPoolSummary[] {
  if (!goals) return [];
  const groups = new Map<string, SavingGoal[]>();
  for (const g of goals) {
    if (g.floatingGroup && g.status === "active") {
      const list = groups.get(g.floatingGroup) ?? [];
      list.push(g);
      groups.set(g.floatingGroup, list);
    }
  }

  const summaries: FloatingPoolSummary[] = [];
  for (const [groupName, groupGoals] of groups.entries()) {
    let totalSaved = 0;
    let totalTarget = 0;
    for (const g of groupGoals) {
      totalSaved += goalCurrentSaved(g);
      totalTarget += g.targetAmount;
    }
    summaries.push({
      groupName,
      totalSaved: roundMoney(totalSaved),
      totalTarget: roundMoney(totalTarget),
      goals: groupGoals,
    });
  }

  return summaries;
}
